import type { JSX } from 'react';
import { BRAND } from '@yuan-math/config';

/**
 * Privacy (§29).
 *
 * Written plainly because the audience includes children and the parents
 * deciding on their behalf. Everything stated here is enforced by the code: the
 * whole profile lives in `localStorage` and there is no network call anywhere
 * in the application.
 */
export function PrivacyPage(): JSX.Element {
  return (
    <div className="page page--reading">
      <header className="page-header">
        <h1 className="page__title">Privacy</h1>
        <p className="page__lead">
          {BRAND.name} is used by children, so privacy was designed in from the first commit rather than added
          later. This page describes what the product actually does, including the one place it contacts anybody
          else.
        </p>
      </header>

      <div className="prose-column">
        <h2 className="prose-heading">What we store</h2>
        <ul className="prose-list">
          <li>A display name the learner chose. A nickname is fine and is what we suggest.</li>
          <li>An age band — not a birthday. It is used to set the size and density of the interface.</li>
          <li>The learner's own work: which lessons they opened, which questions they answered, and how it went.</li>
        </ul>

        <h2 className="prose-heading">What we do not store</h2>
        <ul className="prose-list">
          <li>No date of birth, school, or home address.</li>
          <li>No advertising identifiers, and no profile of a learner for any purpose but teaching them.</li>
          <li>Nothing is ever sold, and nothing is shared with an advertiser.</li>
        </ul>

        <h2 className="prose-heading">How we count visitors</h2>
        <p className="prose">
          We use <strong>Cloudflare Web Analytics</strong> to see how many people visit and which pages they
          read. It is the only third-party code on this site, and it is here because running a site with no idea
          whether anyone can find it is not sustainable.
        </p>
        <p className="prose">
          It is deliberately the least invasive option we could find:
        </p>
        <ul className="prose-list">
          <li>
            <strong>No cookies.</strong> Nothing is stored on your device by it, so there is no consent banner
            because there is nothing to consent to.
          </li>
          <li>
            <strong>No fingerprinting</strong>, and no attempt to recognise the same person twice.
          </li>
          <li>
            <strong>No following you elsewhere.</strong> It cannot see any site but this one.
          </li>
          <li>
            It records a page address, a referrer, a country, and a browser and device type — in aggregate. Not
            who you are, and never what a child answered.
          </li>
        </ul>
        <p className="prose">
          Cloudflare processes this on our behalf and does not use it to build advertising profiles. Their{' '}
          <a href="https://www.cloudflare.com/web-analytics/" target="_blank" rel="noreferrer noopener">
            description of the product
          </a>{' '}
          sets out what it does.
        </p>
        <p className="prose prose--muted">
          If that is one third party too many for you, a content blocker will stop it and the site works exactly
          as before — nothing here depends on it.
        </p>

        <h2 className="prose-heading">Where it lives</h2>
        <p className="prose">
          In this version, everything is stored in this browser, on this device. It is not transmitted anywhere,
          because there is no server to transmit it to. Clearing your browser storage deletes it completely.
        </p>

        <h2 className="prose-heading">Your controls</h2>
        <ul className="prose-list">
          <li>
            <strong>Export.</strong> The parent view can download everything held about a learner as a readable JSON
            file.
          </li>
          <li>
            <strong>Deletion.</strong> The same view deletes it permanently, immediately, with no retention period.
          </li>
        </ul>

        <h2 className="prose-heading">When accounts arrive</h2>
        <p className="prose">
          Sign-in is handled by an established provider rather than by passwords we store ourselves. Child
          accounts require parental consent, collect only the minimal fields listed above, and keep export and
          deletion available from the same place. No advertising will ever be introduced, and no analytics that
          identifies an individual learner.
        </p>

        <p className="prose prose--muted">
          This page describes the product as built. It is written to be read, not to be legally defensible — a formal
          policy accompanies the first release that stores data off-device.
        </p>
      </div>
    </div>
  );
}
